import type {
  CanalDetalhe,
  CanalResumo,
  ContatoInterno,
  DestinoUpload,
  DetalhesDaConversa,
  GrupoForm,
  MensagemCanalChat,
  PrazoDeFixacao,
  ResultadoBuscaInterna,
  SecaoDeConversas,
} from '@mymaxx/shared';
import { api } from './api';
import { lerAccessToken } from './sessao';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** A conversa interna da equipe: canais, mensagens e a agenda de quem se pode chamar. */
const base = '/v1/interno';

export const internoApi = {
  canais: () => api.get<CanalResumo[]>(`${base}/canais`),
  detalhe: (id: string) => api.get<CanalDetalhe>(`${base}/canais/${id}`),
  contatos: (busca?: string) =>
    api.get<ContatoInterno[]>(
      `${base}/contatos${busca ? `?busca=${encodeURIComponent(busca)}` : ''}`,
    ),
  /** Abre a conversa com uma pessoa; a segunda vez devolve a mesma. */
  abrirDireto: (com: { usuarioId?: string; clienteId?: string }) =>
    api.post<CanalDetalhe>(`${base}/canais/direto`, com),
  criarGrupo: (form: GrupoForm) => api.post<CanalDetalhe>(`${base}/canais/grupo`, form),
  escrever: (canalId: string, texto: string, respondeAId?: string) =>
    api.post<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens`, { texto, respondeAId }),
  marcarLido: (canalId: string) => api.post<void>(`${base}/canais/${canalId}/lido`),
  /** Corrige o texto de uma mensagem minha; o fio mostra que foi editada. */
  editar: (canalId: string, mensagemId: string, texto: string) =>
    api.patch<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens/${mensagemId}`, { texto }),
  apagar: (canalId: string, mensagemId: string) =>
    api.delete<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens/${mensagemId}`),
  adicionarMembros: (canalId: string, membros: { usuarioIds?: string[]; clienteIds?: string[] }) =>
    api.post<void>(`${base}/canais/${canalId}/membros`, {
      usuarioIds: membros.usuarioIds ?? [],
      clienteIds: membros.clienteIds ?? [],
    }),
  sair: (canalId: string) => api.delete<void>(`${base}/canais/${canalId}/membros`),

  /** Tira alguém do grupo. Admin tira membro comum; só o dono tira um admin. */
  removerMembro: (canalId: string, alvo: { tipo: 'usuario' | 'tecnico'; id: string }) =>
    api.delete<void>(`${base}/canais/${canalId}/membros/${alvo.tipo}/${alvo.id}`),

  /** Procura no que foi dito, nos grupos e na agenda — tudo de uma vez. */
  buscar: (termo: string) =>
    api.get<ResultadoBuscaInterna>(`${base}/busca?termo=${encodeURIComponent(termo)}`),

  /** Cria (ou devolve) o grupo da equipe de um setor. */
  grupoDoSetor: (setorId: string) =>
    api.post<{ id: string; nome: string; membros: number }>(`${base}/setores/${setorId}/grupo`),

  /**
   * Baixa o histórico da conversa em CSV.
   *
   * Não usa o helper `api` porque a resposta é um arquivo, e não JSON — o mesmo caminho da
   * transcrição do atendimento.
   */
  async baixarHistorico(canalId: string, nome: string): Promise<void> {
    const resposta = await fetch(`${BASE_URL}${base}/canais/${canalId}/historico.csv`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${lerAccessToken() ?? ''}` },
    });
    if (!resposta.ok) throw new Error('Não consegui baixar o histórico.');
    const url = URL.createObjectURL(await resposta.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversa-${nome.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  definirPalavras: (canalId: string, palavras: string[]) =>
    api.put<string[]>(`${base}/canais/${canalId}/palavras`, { palavras }),

  encaminhar: (canalId: string, mensagemId: string, paraCanalId: string) =>
    api.post<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens/${mensagemId}/encaminhar`, {
      paraCanalId,
    }),

  /** Mídias, guardadas e grupos em comum — tudo o que o painel da conversa mostra. */
  detalhes: (canalId: string) => api.get<DetalhesDaConversa>(`${base}/canais/${canalId}/detalhes`),

  guardar: (canalId: string, mensagemId: string) =>
    api.post<{ guardada: boolean }>(`${base}/canais/${canalId}/mensagens/${mensagemId}/guardada`),

  /** Prende a mensagem no alto da conversa, com prazo. */
  fixarMensagem: (canalId: string, mensagemId: string, prazo: PrazoDeFixacao) =>
    api.post<void>(`${base}/canais/${canalId}/mensagens/${mensagemId}/fixar`, { prazo }),

  desafixarMensagem: (canalId: string, mensagemId: string) =>
    api.delete<void>(`${base}/canais/${canalId}/mensagens/${mensagemId}/fixar`),

  /** Nome e descrição do grupo. Dono e admin. */
  editarGrupo: (canalId: string, dados: { nome: string; descricao: string | null }) =>
    api.patch<void>(`${base}/canais/${canalId}/grupo`, dados),

  /** Promover a admin, ou rebaixar a membro. Só o dono. */
  definirPapel: (
    canalId: string,
    alvo: { tipo: 'usuario' | 'tecnico'; id: string },
    papel: 'admin' | 'membro',
  ) =>
    api.post<void>(`${base}/canais/${canalId}/papel`, {
      ...(alvo.tipo === 'usuario' ? { usuarioId: alvo.id } : { clienteId: alvo.id }),
      papel,
    }),

  /** A foto do grupo, em dois passos como a do perfil. */
  async enviarFotoDoGrupo(canalId: string, arquivo: File) {
    const destino = await api.post<{ chave: string; url: string }>(
      `${base}/canais/${canalId}/foto/preparar`,
      { mimeType: arquivo.type, nome: arquivo.name, tamanho: arquivo.size },
    );
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar a foto. Tente de novo.');
    await api.post<void>(`${base}/canais/${canalId}/foto`, { chave: destino.chave });
  },

  removerFotoDoGrupo: (canalId: string) => api.delete<void>(`${base}/canais/${canalId}/foto`),

  /** Favoritar a CONVERSA: dourada na lista e dentro do filtro "Favoritos". */
  favoritarCanal: (canalId: string, favorito: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/favoritar`, { favorito }),

  arquivar: (canalId: string, arquivado: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/arquivar`, { arquivado }),

  secoes: () => api.get<SecaoDeConversas[]>(`${base}/secoes`),
  criarSecao: (nome: string) => api.post<SecaoDeConversas>(`${base}/secoes`, { nome }),
  renomearSecao: (secaoId: string, nome: string) =>
    api.patch<SecaoDeConversas>(`${base}/secoes/${secaoId}`, { nome }),
  apagarSecao: (secaoId: string) => api.delete<void>(`${base}/secoes/${secaoId}`),
  guardarEmSecao: (canalId: string, secaoId: string | null) =>
    api.post<void>(`${base}/canais/${canalId}/secao`, { secaoId }),

  fixar: (canalId: string, fixado: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/fixar`, { fixado }),
  /** `horas` nulo = até eu desligar. */
  silenciar: (canalId: string, horas: number | null) =>
    api.post<void>(`${base}/canais/${canalId}/silenciar`, { horas }),
  tirarSilencio: (canalId: string) => api.delete<void>(`${base}/canais/${canalId}/silenciar`),

  /** Anexo em dois passos: pede a URL, manda o arquivo direto ao storage e confirma. */
  async enviarArquivo(canalId: string, arquivo: File, legenda?: string) {
    const destino = await api.post<DestinoUpload>(`${base}/canais/${canalId}/anexos/preparar`, {
      nome: arquivo.name,
      mimeType: arquivo.type,
      tamanho: arquivo.size,
    });
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar o arquivo.');
    return api.post<MensagemCanalChat>(`${base}/canais/${canalId}/anexos`, {
      chave: destino.chave,
      nome: arquivo.name,
      mimeType: arquivo.type,
      legenda: legenda ?? '',
    });
  },
};
